import { AnimatePresence, motion } from "framer-motion";
import { Bell, Globe, Shield, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "../../lib/utils";
import GeneralContent from "./refactors/GeneralContent";
import NotificationsContent from "./refactors/NotificationsContent";
import ProfileContent from "./refactors/ProfileContent";
import SecurityContent from "./refactors/SecurityContent";

const CompanySettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo(
    () => [
      {
        id: "profile",
        label: "Profile",
        description: "Name, contact details, and photo",
        icon: User,
      },
      {
        id: "general",
        label: "General",
        description: "Company preferences and shortcuts",
        icon: Globe,
      },
      {
        id: "notifications",
        label: "Notifications",
        description: "Email and in-app alerts",
        icon: Bell,
      },
      {
        id: "security",
        label: "Security",
        description: "Password, 2FA, and sessions",
        icon: Shield,
      },
    ],
    []
  );
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabs.some((tab) => tab.id === tabFromUrl) ? tabFromUrl! : "profile"
  );

  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setSearchParams(id === "profile" ? {} : { tab: id }, { replace: true });
  };

  useEffect(() => {
    if (tabFromUrl && tabs.some((tab) => tab.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, tabs]);

  const active = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  const renderContent = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileContent />;
      case "general":
        return <GeneralContent />;
      case "notifications":
        return <NotificationsContent />;
      case "security":
        return <SecurityContent />;
      default:
        return <ProfileContent />;
    }
  };

  return (
    <div className="min-h-full bg-[#F6F7F4] p-6">
      <div className="mx-auto w-full max-w-6xl">
        <header className="mb-6">
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-primary">
            Workspace
          </p>
          <h1 className="mt-1 text-[28px] font-semibold tracking-tight text-hColor">
            Settings
          </h1>
          <p className="mt-1 max-w-xl text-sm text-pColor">
            Manage your profile, company preferences, alerts, and how you sign
            in.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
          <nav className="flex gap-2 overflow-x-auto pb-1 lg:sticky lg:top-6 lg:flex-col lg:overflow-visible lg:pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "flex min-w-[200px] items-center gap-3 rounded-2xl px-3.5 py-3 text-left transition-all lg:min-w-0",
                    isActive
                      ? "bg-white text-hColor shadow-[0_1px_2px_rgba(28,28,28,0.06)] ring-1 ring-[#EEEFEA]"
                      : "text-pColor hover:bg-white/70 hover:text-hColor"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                      isActive
                        ? "bg-primary text-white"
                        : "bg-[#EDF2DF] text-primary"
                    )}
                  >
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">
                      {tab.label}
                    </span>
                    <span className="mt-0.5 hidden text-[12px] leading-snug text-pColor lg:block">
                      {tab.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="min-w-0">
            <div className="mb-4 lg:hidden">
              <h2 className="text-lg font-semibold text-hColor">
                {active.label}
              </h2>
              <p className="text-sm text-pColor">{active.description}</p>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
